import { AddCircleIcon, CoinPlusIcon, ListIcon, TruckIcon } from '@synergy/theme-next';
import { AddRowIcon } from '@synergy/theme';
import { DocumentStatus } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PoolButton, PoolSplitButton } from '@synergy/components-next';
import { Stack } from '@mui/material';
import { usePaloThemeGuard } from '@synergy/theme-palo';
import { useState } from 'react';

import { InvoiceGenericItemType } from '../../models/invoice-generic-item';
import { PaloAddPositionsDialog } from '../../../../common/components/palo/palo-add-positions-dialog';
import { invoiceDetailOverviewDataSelector } from '../store/overview/invoice-detail-overview-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useInvoiceDetailContext } from '../../context/invoice-detail-context';

export interface PaloInvoicePositionToolsProps {
    /** Puts an empty row under the table, which then carries the article search. */
    onAddRow?: () => void;
}

/** Position toolbar of the invoice editor (split off the legacy InvoiceDetailCtaButton). */
export const PaloInvoicePositionTools = ({ onAddRow }: PaloInvoicePositionToolsProps): JSX.Element => {
    const [showAddMultiple, setShowAddMultiple] = useState(false);
    const invoice = useAppSelector(invoiceDetailOverviewDataSelector);
    usePaloThemeGuard('PaloInvoicePositionTools');
    const {
        supplierAccountId,
        supplierAccountName,
        setSelectedProduct,
        setShowAddPositionModal,
        setIsNewTextItem,
        setShowNewTextItemModal,
        setShowTaxRateOverrideModal,
        setShowGenericItemModal,
    } = useInvoiceDetailContext();

    const handleNewline = () => {
        setIsNewTextItem?.(true);
        setShowNewTextItemModal?.(true);
    };

    /*
     * A cancelled invoice is finished for good — nothing will ever be added
     * to it again, so the buttons go rather than sit there greyed out. A
     * completed one can be unlocked, so there they stay and say they are
     * closed for now (GF 13.10.2026, #2210).
     */
    const cancelled = invoice.values.status === DocumentStatus.Canceled;
    const disabledPositionSettings = invoice.values.status === DocumentStatus.Completed || cancelled;

    /**
     * The supplier the head holds right now; the saved one only where the head
     * is not on screen (Prüfbericht 28.09.2026, T4).
     */
    const supplierId =
        undefined !== supplierAccountId ? supplierAccountId ?? undefined : invoice.values.supplierAccount?.id;
    const supplierName = undefined !== supplierAccountId ? supplierAccountName : invoice.values.supplierAccount?.name;

    if (cancelled) {
        return <></>;
    }

    return (
        <Stack
            direction="row"
            gap={2}
            alignItems="flex-end"
            data-palo-anchor="invoice-position-tools"
            sx={{ flex: 1, minWidth: 530, maxWidth: 700 }}
        >
            {/* Quiet, like the order's: adding a position is not the loudest
                thing on the mask, and the blue belongs to saving. */}
            <PoolSplitButton
                variant="outlined"
                color="inherit"
                preSelectedIndex={0}
                menuItems={[
                    {
                        label: <FormattedMessage id="app.positions.addRow" />,
                        displayLabel: <FormattedMessage id="app.positions.addRow" />,
                        icon: <AddCircleIcon />,
                        onClick: () => onAddRow?.(),
                    },
                    {
                        label: <FormattedMessage id="app.invoices.detail.addFreePosition" />,
                        displayLabel: <FormattedMessage id="app.invoices.detail.addFreePositionShort" />,
                        icon: <CoinPlusIcon />,
                        onClick: () => setShowAddPositionModal?.(true),
                    },
                    {
                        label: <FormattedMessage id="app.invoices.detail.taxRate.title" />,
                        displayLabel: <FormattedMessage id="app.invoices.detail.taxRate.titleShort" />,
                        icon: <ListIcon />,
                        onClick: () => setShowTaxRateOverrideModal?.(true),
                    },
                    {
                        label: <FormattedMessage id="app.invoices.detail.newLine" />,
                        displayLabel: <FormattedMessage id="app.invoices.detail.newLineShort" />,
                        icon: <AddRowIcon htmlColor="inherit" />,
                        onClick: () => handleNewline(),
                    },
                ]}
                sx={{ flexShrink: 0 }}
                disabled={disabledPositionSettings}
            />
            {/* The article search lives in here now, the way ERPNext keeps it
                in "Add multiple": pick, the row appears, pick the next — the
                dialog stays open. Flat on the mask it was the same thing
                without a lid (#2208). */}
            <PoolButton
                variant="outlined"
                color="inherit"
                startIcon={<AddCircleIcon />}
                disabled={disabledPositionSettings}
                onClick={() => setShowAddMultiple(true)}
            >
                <FormattedMessage id="app.positions.addMultiple" />
            </PoolButton>
            <PaloAddPositionsDialog
                open={showAddMultiple}
                onClose={() => setShowAddMultiple(false)}
                onSelect={product => setSelectedProduct?.(product)}
                supplierIds={supplierId ? [supplierId] : undefined}
                supplierName={supplierName}
                supplierMissing={!supplierId}
                includeNormArticles={false}
            />
        </Stack>
    );
};
